import { createContext, useContext } from "react";
import type { SalesCallDraft } from "@/Features/ViewCompany/Helpers/viewCompanySalesCallHelpers";

export interface EssentialCompanyData {
  companyId: string;
  companyName: string;
  temperature: string | undefined;
  shipmentFrequency: string | undefined;
  shipmentFrequencyNote: string | undefined;
  controlsLtlFreight: string | undefined;
  assignedTo: string | undefined;
  customerSuccessAssignedTo: string | undefined;
  estimatedMonthlyFreightSpend: number | undefined;
  estimatedMonthlyShipments: number | undefined;
  monthlyLtlCaptureEstimate: number | undefined;
  defaultSalesContactId: string | undefined;
  companyLabelIds: string[];
}

export interface CallData {
  companyNoteId: string;
  company: EssentialCompanyData;
  draft: SalesCallDraft;
  isSaving: boolean;
}

export interface CallContextValue {
  callData: CallData | undefined;
  /**
   * Whether the Log Sales Call dialog is open. Lives here (not in the floating
   * button) so the incoming-call banner can open the dialog immediately on
   * "Log Call" instead of making the rep click the floating button first.
   * Per-tab state — the draft itself is what persists across tabs.
   */
  logDialogOpen: boolean;
  setLogDialogOpen: (open: boolean) => void;
  startCall: (
    companyNoteId: string,
    company: EssentialCompanyData,
    initialDraft?: Partial<SalesCallDraft>,
  ) => void;
  endCall: () => void;
  updateDraft: <K extends keyof SalesCallDraft>(
    field: K,
    value: SalesCallDraft[K],
  ) => void;
  setSaving: (saving: boolean) => void;
  registerRefreshCallback: (cb: () => void) => void;
  unregisterRefreshCallback: (cb: () => void) => void;
}

const CallContext = createContext<CallContextValue | undefined>(undefined);

export function useCallContext(): CallContextValue {
  const ctx = useContext(CallContext);
  if (!ctx) throw new Error("useCallContext must be used within CallProvider");
  return ctx;
}

export { CallContext };
